import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useT } from "@/glossary";
import { cn } from "@/lib/cn";

/**
 * Bottom bar of the listening exercises (Train, Omo): a start button, then the
 * replay button, which shrinks to make room for "Next" once answered.
 */
export const ListeningControls = ({
  hasStarted,
  onStart,
  hasAnswered,
  replayButton,
  onNext,
  nextLabel,
}: {
  hasStarted: boolean;
  onStart: () => void;
  hasAnswered: boolean;
  replayButton: ReactNode;
  onNext: () => void;
  nextLabel: string;
}) => {
  const t = useT();

  return (
    <div className="flex items-stretch gap-4 overflow-x-hidden pt-4">
      {!hasStarted ? (
        <Button
          priority
          onClick={onStart}
          className="min-h-15 w-full rounded-full px-8 py-5 font-bold text-lg shadow-[0_4px_12px_rgba(0,0,0,0.12)]"
        >
          {t.common.clickToStart}
        </Button>
      ) : (
        <>
          <div
            className={cn(
              "origin-left transition-[width,transform] duration-300 ease-out",
              hasAnswered ? "w-1/2 scale-[0.985]" : "w-full scale-100",
            )}
          >
            {replayButton}
          </div>
          <div
            className={cn(
              "overflow-hidden bg-transparent transition-[width,opacity,transform] duration-300 ease-out",
              hasAnswered
                ? "w-1/2 translate-x-0 opacity-100"
                : "pointer-events-none w-0 translate-x-4 opacity-0",
            )}
            aria-hidden={!hasAnswered}
          >
            <Button
              onClick={onNext}
              className="min-h-15 w-full whitespace-nowrap rounded-full border-neutral-950 bg-white px-8 py-5 font-bold text-lg text-neutral-950 shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-colors duration-200 hover:bg-neutral-50 active:bg-neutral-100"
              tabIndex={hasAnswered ? 0 : -1}
            >
              {nextLabel}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
